import React, { useContext, useState, useEffect } from 'react'
import { ArrowLeft, ArrowRight, OctagonX } from 'lucide-react'
import { motion } from 'framer-motion'
import { ThemeContext } from '../context/ThemeProvider'

const NavBar = ({
  type,
  onNext,
  onPrevious,
  currentIndex,      // number
  dataSet,           // total number of items
  navTitle = "Question",
  showEnd = false,
  onEndTest,
  isLoading = false
}) => {
  const { theme } = useContext(ThemeContext);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(window.innerWidth >= 1024); // lg breakpoint
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const total = dataSet
  const isFirst = currentIndex === 0
  const isLast = currentIndex === total - 1
  const progressPercent = ((currentIndex + 1) / total) * 100

  const styles = {
    container: {
      position: 'fixed',
      bottom: isLargeScreen ? '.5rem' : '5rem',
      left: isLargeScreen ? '200px' : 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 1.5rem',
      zIndex: 50,
    },
    navbar: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      maxWidth: '48rem',
      background: theme.dashboard.card.background,
      border: `1px solid ${theme.dashboard.card.border}`,
      borderRadius: '1rem',
      boxShadow: theme.dashboard.card.shadow,
      paddingLeft: '1rem',
      paddingRight: '1rem',
      paddingTop: '0.5rem',
      paddingBottom: '0.5rem',
      backdropFilter: 'blur(20px)',
    },
    progressContainer: {
      flex: 1,
      margin: '0 0.5rem',
    },
    titleText: {
      textAlign: 'center',
      marginBottom: '0.5rem',
      fontWeight: '600',
      fontSize: '0.875rem',
      color: theme.text.primary,
    },
    progressBar: {
      position: 'relative',
      height: '0.5rem',
      borderRadius: '0.25rem',
      overflow: 'hidden',
      background: theme.primary.gray[300],
      marginBottom: '0.75rem',
    },
    progressFill: {
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      background: `linear-gradient(90deg, ${theme.primary.blue[500]}, ${theme.primary.blue[600]})`,
      transition: 'width 0.3s ease-in-out',
      borderRadius: '0.25rem',
    },
    progressDot: {
      position: 'absolute',
      width: '0.75rem',
      height: '0.75rem',
      borderRadius: '50%',
      background: theme.neutral.white,
      border: `2px solid ${theme.primary.blue[600]}`,
      boxShadow: theme.dashboard.card.shadow,
      top: '50%',
      transform: 'translate(-50%, -50%)',
      transition: 'left 0.3s ease-in-out',
    },
    numbersContainer: {
      position: 'relative',
      height: '1rem',
    },
    numberLabel: {
      position: 'absolute',
      fontSize: '0.75rem',
      color: theme.text.tertiary,
      transform: 'translateX(-50%)',
      transition: 'all 0.2s ease-in-out',
    },
    currentNumberLabel: {
      fontWeight: '700',
      color: theme.primary.blue[600],
      transform: 'translateX(-50%) scale(1.1)',
    },
    endButton: {
      position: 'absolute',
      left: '100%',
      marginLeft: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      whiteSpace: 'nowrap',
    },
    navigationButtons: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    }
  };

  const renderProgressNumbers = () => {
    if (total > 10) {
      // For many items, show key milestones
      const milestones = [1, Math.ceil(total / 4), Math.ceil(total / 2), Math.ceil((total * 3) / 4), total];
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {milestones.map((val, i) => (
            <span
              key={i}
              style={{
                ...styles.numberLabel,
                position: 'static',
                transform: 'none',
                ...(val === currentIndex + 1 ? styles.currentNumberLabel : {})
              }}
            >
              {val}
            </span>
          ))}
        </div>
      );
    } else {
      // For few items, show all numbers
      return (
        <div style={styles.numbersContainer}>
          {Array.from({ length: total }).map((_, idx) => (
            <span
              key={idx}
              style={{
                ...styles.numberLabel,
                left: `${(idx / (total - 1)) * 100}%`,
                ...(idx === currentIndex ? styles.currentNumberLabel : {})
              }}
            >
              {idx + 1}
            </span>
          ))}
        </div>
      );
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        {/* Previous Button */}
        <button
          disabled={isFirst || isLoading}
          onClick={onPrevious}
          style={{ 
            width: '3rem', 
            height: '3rem',
            padding: '0',
            borderRadius: '50%',
            background: isLoading ? theme.primary.gray[300] : theme.primary.blue[500],
            color: theme.text.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: (isFirst || isLoading) ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
          }}
        >
          <ArrowLeft size={18} />
        </button>

        {/* Progress Section */}
        <div style={styles.progressContainer}>
          <div style={styles.titleText}>
            {navTitle} {currentIndex + 1} of {total}
          </div>

          <div style={styles.progressBar}>
            {/* Progress Fill */}
            <div
              style={{
                ...styles.progressFill,
                width: `${((currentIndex) / (total - 1)) * 100}%`,
              }}
            />
            {/* Progress Dot */}
            <div
              style={{
                ...styles.progressDot,
                left: `${((currentIndex) / (total - 1)) * 100}%`,
              }}
            />
          </div>

          {/* Progress Numbers */}
          {renderProgressNumbers()}
        </div>

        {/* Next Button */}
        <div style={styles.navigationButtons}>
          <button
            disabled={isLast || isLoading}
            onClick={onNext}
            style={{ 
              width: '3rem', 
              height: '3rem',
              padding: '0',
              borderRadius: '50%',
              background: isLoading ? theme.primary.gray[300] : theme.primary.blue[500],
              color: theme.text.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: (isLast || isLoading) ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            <ArrowRight size={18} />
          </button>

          {/* End Test Button */}
          {showEnd && (
            <button
              onClick={onEndTest}
              disabled={isLoading}
              style={{
                background: isLoading ? theme.primary.gray[300] : theme.primary.red[500],
                color: theme.text.primary,
                borderRadius: '0.5rem',
                padding: '0.5rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '0.875rem',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                  Processing...
                </>
              ) : (
                <>
                  <OctagonX size={16} />
                  End Test
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default NavBar